import React from 'react'
import { useSelector } from 'react-redux'
import { useFormik, getIn } from 'formik'
import * as Yup from 'yup'
import { RootState } from 'index'
import { FuelTypes } from 'shared/constants'
import { Row, Col, Input, Button } from 'reactstrap'
import FormValuesDebug from 'utils/debugTools/FormValuesDebug'
import ErrorTooltip from 'Components/ErrorTooltip'
import env from 'environment_system/env_system'
import { commonValidationMessages } from 'Components/ValidationErrorMessages'
import apiGlobal from 'global/api.global'
import { errorToast, successToast } from 'Components/Toasts'
import { queryKeyes } from 'shared/queryKeys'
import { queryClient } from 'react-query/queryClient'
import { dateTimeFormat } from 'GenericForms/Helper'
import { useQuery } from 'react-query'
import { Get } from 'VesselMaster/vesselMaster.hooks'
import TableLoading from 'Components/TableLoading'
import ErrorComponent from 'Components/ErrorComponent'
interface OffhireFormProps {
    setState: (state: boolean) => void
    values?: any
}

const OffhierForm = ({ setState, values }: OffhireFormProps) => {
    const { VoyageID, Voyages, VesselID } = useSelector((state: RootState) => state.Reporting)
    const voyage: any = Voyages.find((rec: any) => rec.id === VoyageID)
    const departureDate = voyage?.departure_date_time ? new Date(voyage.departure_date_time) : null

    const getInitialValues = (values: any, filteredOptions: any, VoyageID: number, VesselID: number) => {
        const initialROBValues = filteredOptions?.reduce((acc: any, fuel: any) => {
            acc[fuel.fuel_type_precedence_id] = {
                fuel_type_name: fuel.fuel_type_name,
                vessel_fuel: fuel.id,
                rob: ''
            }
            return acc
        }, {})

        if (values) {
            return {
                id: values.id,
                voyage_information: values.voyage_information ?? VoyageID,
                vessel: values.vessel ?? VesselID,
                start_date_time: values.start_date_time
                    ? dateTimeFormat(values.start_date_time)
                    : '',
                end_date_time: values.end_date_time ? dateTimeFormat(values.end_date_time) : '',
                start_rob: filteredOptions?.reduce((acc: any, fuel: any) => {
                    acc[fuel.fuel_type_precedence_id] = {
                        fuel_type_name: fuel.fuel_type_name,
                        vessel_fuel: fuel.id,
                        rob: values.start_rob?.[fuel.fuel_type_precedence_id]?.rob ?? ''
                    }
                    return acc
                }, {}),
                end_rob: filteredOptions?.reduce((acc: any, fuel: any) => {
                    acc[fuel.fuel_type_precedence_id] = {
                        fuel_type_name: fuel.fuel_type_name,
                        vessel_fuel: fuel.id,
                        rob: values.end_rob?.[fuel.fuel_type_precedence_id]?.rob ?? ''
                    }
                    return acc
                }, {}),
            }
        }

        // Default blank values for create mode
        return {
            id: 0,
            voyage_information: VoyageID,
            vessel: VesselID,
            start_date_time: '',
            end_date_time: '',
            start_rob: initialROBValues,
            end_rob: initialROBValues,
        }
    }
    const { data: FuelType, isLoading, isError } = useQuery(
        [queryKeyes.vessel.vesselFuelType.key, VesselID],
        async () => await Get(queryKeyes.vessel.vesselFuelType.url(VesselID), VesselID),
        {
            enabled: true,
            staleTime: Infinity,
        }
    )

    const filteredOptions = FuelType?.filter(
        (item: any) => item?.fuel_sub_type_precedence_id !== FuelTypes.LNG_CARGO
    )

    const { data: reports, isLoading: reportsLoading, isError: reportsError } = useQuery(
        [queryKeyes.vessel.vesselVoyageReporting.key, VesselID, VoyageID],
        async () => { return await Get(queryKeyes.vessel.vesselVoyageReporting.url(VesselID, VoyageID), VesselID, VoyageID); },
        { staleTime: Infinity }
    )
    // If voyage end exists, use it. Otherwise use last report time.
    const voyageEndDate = React.useMemo(() => {
        // 1. Primary value: voyage end date
        if (voyage?.voyage_end_date_time) {
            return new Date(voyage.voyage_end_date_time);
        }

        // 2. Fallback: find latest report from reports array
        if (reports && Array.isArray(reports) && reports.length > 0) {
            // Sort descending by reporting_time_utc
            const lastReport = [...reports]
                .filter(r => r?.reporting_time_utc)
                .sort((a, b) =>
                    new Date(b.reporting_time_utc).getTime() - new Date(a.reporting_time_utc).getTime()
                )[0];

            if (lastReport) {
                return new Date(lastReport.reporting_time_utc);
            }
        }

        // 3. Nothing available
        return null;
    }, [voyage, reports]);

    const robValidation = Yup.object().shape({
        start_rob: Yup.array().of(
            Yup.object({
                rob: Yup.string()
                    .nullable()
                    .matches(/^(?:0|[1-9]\d{0,6})(?:\.\d{1,4})?$/, commonValidationMessages.positiveROB)
                    .required(commonValidationMessages.required)
            }).nullable()
        ),

        end_rob: Yup.array().of(
            Yup.object({
                rob: Yup.string()
                    .nullable()
                    .matches(/^(?:0|[1-9]\d{0,6})(?:\.\d{1,4})?$/, commonValidationMessages.positiveROB)
                    .required(commonValidationMessages.required)
            }).nullable()
        )
    });

    const formik: any = useFormik({
        initialValues: getInitialValues(values, filteredOptions, VoyageID, VesselID),
        validationSchema: Yup.object({
            start_date_time: Yup.string()
                .required('Start date is required')
                .test('after-departure', 'Start date must be after voyage departure date', function (value) {
                    if (!departureDate || !value) return true
                    return new Date(value) >= departureDate
                })
                .test('before-voyage-end', 'Start date must be before voyage end date', function (value) {
                    if (!voyageEndDate || !value) return true
                    return new Date(value) <= voyageEndDate
                }),

            end_date_time: Yup.string()
                .required('End date is required')
                .test('after-start', 'End date must be after start date', function (value) {
                    const { start_date_time }: any = this.parent
                    if (!start_date_time || !value) return true
                    return new Date(value) > new Date(start_date_time)
                })
                .test('before-voyage-end', 'End date must be before voyage end date', function (value) {
                    if (!voyageEndDate || !value) return true
                    return new Date(value) <= voyageEndDate
                }),

            start_rob: robValidation,
            end_rob: robValidation,
        }),
        onSubmit: (values: any, actions: any) => {
            actions.setSubmitting(true);
            if (values.id > 0) {
                apiGlobal.put(queryKeyes.offHire.OffHirePeriodput.url(values.id), values).then(async () => {
                    successToast("Offhire period updated successfully");
                    await queryClient.invalidateQueries(queryKeyes.offHire.OffHirePeriod.key);
                    await queryClient.invalidateQueries(queryKeyes.offHire.OffHirePeriodByVoyage.key);
                    await queryClient.invalidateQueries(queryKeyes.offHire.OffHirePeriodByVessel.key);
                    setState(false);
                }).catch((err) => {
                    console.error(err);
                    errorToast(err.response.data.error);
                }).finally(() => {
                    actions.setSubmitting(false);
                });
            } else {
                apiGlobal.post(queryKeyes.offHire.OffHirePeriod.url(), values).then(async () => {
                    successToast("Offhire period created successfully");
                    formik.resetForm();
                    await queryClient.invalidateQueries(queryKeyes.offHire.OffHirePeriodByVoyage.key);
                    await queryClient.invalidateQueries(queryKeyes.offHire.OffHirePeriodByVessel.key);
                    setState(false);
                }).catch((err) => {
                    console.error(err);
                    errorToast(err.response.data.error);
                }).finally(() => {
                    actions.setSubmitting(false);
                });
            }
        },
    })

    return (
        <div className="p-1">
            <form onSubmit={formik.handleSubmit} autoComplete='off'>
                {(isLoading || reportsLoading) && <TableLoading Rows={2} />}
                {(isError || reportsError) && <ErrorComponent message="Unable to load required data!" />}
                {!isLoading && !isError && !reportsLoading && !reportsError && (
                    <div className="table-responsive">
                        <table className="table mt-2 align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th style={{ width: '20%' }}>Duration</th>
                                    <th style={{ width: '25%' }}>Date & Time</th>
                                    {filteredOptions?.map((fuel: any) => (
                                        <th key={fuel.fuel_type_precedence_id} className="text-center">
                                            {fuel.fuel_type_name}
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody>
                                {/* START ROW */}
                                <tr>
                                    <td>Start</td>
                                    <td>
                                        <Input
                                            type="datetime-local"
                                            name="start_date_time"
                                            id="start_date_time"
                                            className="datetimepicker text-uppercase"
                                            min={departureDate ? departureDate.toISOString().slice(0, 16) : undefined}
                                            max={voyageEndDate ? voyageEndDate.toISOString().slice(0, 16) : undefined}
                                            defaultValue={formik.values.start_date_time}
                                            onChange={formik.handleChange}
                                        />
                                        {formik.errors.start_date_time && env.form_validation && (
                                            <ErrorTooltip
                                                message={formik.errors.start_date_time}
                                                target="start_date_time"
                                                open={true}
                                            />
                                        )}
                                    </td>
                                    {filteredOptions?.map((fuel: any) => {
                                        const fieldName = `start_rob.${fuel.fuel_type_precedence_id}.rob`
                                        // Create a valid ID by replacing dots with underscores
                                        const fieldId = `start_rob_${fuel.fuel_type_precedence_id}_rob`
                                        const fieldError = getIn(formik.errors, fieldName)
                                        const fieldTouched = getIn(formik.touched, fieldName)

                                        return (
                                            <td key={`start_${fuel.fuel_type_precedence_id}`}>
                                                <Input
                                                    type="text"
                                                    name={fieldName}
                                                    id={fieldId}
                                                    value={formik?.values?.start_rob?.[fuel.fuel_type_precedence_id]?.rob}
                                                    onChange={formik?.handleChange}
                                                    onBlur={formik?.handleBlur}
                                                    placeholder="Start ROB"
                                                />
                                                {fieldTouched && fieldError && env.form_validation && (
                                                    <ErrorTooltip
                                                        message={fieldError}
                                                        target={fieldId}
                                                        open={true}
                                                    />
                                                )}
                                            </td>
                                        )
                                    })}
                                </tr>

                                {/* END ROW */}
                                <tr>
                                    <td>End</td>
                                    <td>
                                        <Input
                                            type="datetime-local"
                                            className="datetimepicker text-uppercase"
                                            name="end_date_time"
                                            id="end_date_time"
                                            min={departureDate ? departureDate.toISOString().slice(0, 16) : undefined}
                                            max={voyageEndDate ? voyageEndDate.toISOString().slice(0, 16) : undefined}
                                            value={formik.values.end_date_time}
                                            onChange={formik.handleChange}
                                        />
                                        {formik.errors.end_date_time && env.form_validation && (
                                            <ErrorTooltip
                                                message={formik.errors.end_date_time}
                                                target="end_date_time"
                                                open={true}
                                            />
                                        )}
                                    </td>
                                    {filteredOptions?.map((fuel: any) => {
                                        const fieldName = `end_rob.${fuel.fuel_type_precedence_id}.rob`
                                        // Create a valid ID by replacing dots with underscores
                                        const fieldId = `end_rob_${fuel.fuel_type_precedence_id}_rob`
                                        const fieldError = getIn(formik.errors, fieldName)
                                        const fieldTouched = getIn(formik.touched, fieldName)

                                        return (
                                            <td key={`end_${fuel.fuel_type_precedence_id}`}>
                                                <Input
                                                    type="text"
                                                    name={fieldName}
                                                    id={fieldId}
                                                    value={formik?.values?.end_rob?.[fuel.fuel_type_precedence_id]?.rob}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    placeholder="End ROB"
                                                />
                                                {fieldTouched && fieldError && env.form_validation && (
                                                    <ErrorTooltip
                                                        message={fieldError}
                                                        target={fieldId}
                                                        open={true}
                                                    />
                                                )}
                                            </td>
                                        )
                                    })}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
                <Row className="mt-3">
                    <Col className="text-end">
                        <Button type="submit" color="primary" disabled={formik.isSubmitting}>
                            {formik.isSubmitting && <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />}Save
                        </Button>
                    </Col>
                </Row>

                <FormValuesDebug values={[formik.values, formik.errors]} />
            </form>
        </div>
    )
}

export default OffhierForm